import type { PathHelper } from '@/hooks/usePathHelper'

type OrganizationId = string | number
type OrganizationIdInput = OrganizationId | null | undefined
type OrganizationPathHelpers = Readonly<
  Record<string, Partial<Pick<PathHelper, 'basePath'>> | null | undefined>
>

const DEFAULT_ORG_SWITCH_PATH = (organizationId: OrganizationId): string =>
  `/${organizationId}/admin`
const EXACT_PATH_PRESERVE_PATTERNS = [
  /^\/[^/]+\/designs\/import$/,
  /^\/[^/]+\/organizations\/[^/]+$/,
  /^\/[^/]+\/products\/collections\/import$/,
  // This route shares its final segment with the top-level subscriptions route,
  // so the generated path helpers cannot represent both safe destinations.
  /^\/[^/]+\/settings\/subscriptions$/,
  // Seasonal Content is global; its organization segment only selects the preview storefront.
  /^\/[^/]+\/seasonal-content\/\d+$/,
]

const replaceOrganizationIdInPathname = (
  pathname: string,
  organizationId: OrganizationId
): string => pathname.replace(/^\/[^/]+/, `/${organizationId}`)

const resolveExactPreservedPath = (
  currentPathname: string,
  organizationId: OrganizationId
): string | null => {
  if (!currentPathname) {
    return null
  }

  return EXACT_PATH_PRESERVE_PATTERNS.some((pattern) => pattern.test(currentPathname))
    ? replaceOrganizationIdInPathname(currentPathname, organizationId)
    : null
}

const hasOnlyOrganizationParam = (fullPath: string | null | undefined): boolean => {
  const params = fullPath?.match(/\$[A-Za-z0-9_]+/g) ?? []

  return params.length > 0 && params.every((param) => param === '$orgId')
}

const routeMatchesCurrentPath = (route: string, pathname: string): boolean => {
  const routeSegments = route.split('/').filter(Boolean)
  const pathSegments = pathname.split('/').filter(Boolean)

  if (routeSegments.length > pathSegments.length) {
    return false
  }

  return routeSegments.every(
    (segment, index) => segment.startsWith('$') || segment === pathSegments[index]
  )
}

const resolveSafeRouteCandidates = (
  currentPathname: string,
  pathHelper: OrganizationPathHelpers
): string[] =>
  Object.values(pathHelper)
    .flatMap((value) => {
      const basePath = value?.basePath

      if (!basePath) {
        return []
      }

      const candidates = [basePath]

      if (currentPathname.endsWith('/create')) {
        candidates.push(`${basePath}/create`)
      }

      return candidates
    })
    .filter(Boolean)
    .filter((route) => route !== '/$orgId')
    .filter(hasOnlyOrganizationParam)

export const resolveOrganizationDeletionRedirectId = <T extends OrganizationId>({
  deletedOrganizationId,
  currentOrganizationId,
  isImpersonating,
  userOrganizationId,
}: {
  deletedOrganizationId?: OrganizationIdInput
  currentOrganizationId?: OrganizationIdInput
  isImpersonating?: boolean
  userOrganizationId?: T | null
}): T | null => {
  if (
    !isImpersonating ||
    !userOrganizationId ||
    Number(deletedOrganizationId) !== Number(currentOrganizationId)
  ) {
    return null
  }

  return userOrganizationId
}

export const resolveOrganizationSwitchPath = ({
  currentPathname,
  organizationId,
  pathHelper,
}: {
  currentPathname: string
  organizationId: OrganizationIdInput
  pathHelper: OrganizationPathHelpers
}): string => {
  if (!organizationId) {
    throw new Error('organizationId is required to resolve an organization switch path.')
  }

  const preservedPath = resolveExactPreservedPath(currentPathname, organizationId)

  if (preservedPath) {
    return preservedPath
  }

  const safeRoute = resolveSafeRouteCandidates(currentPathname, pathHelper)
    .filter((route) => routeMatchesCurrentPath(route, currentPathname))
    .sort((left, right) => right.split('/').length - left.split('/').length)[0]

  if (safeRoute) {
    return safeRoute.replace('$orgId', `${organizationId}`)
  }

  return DEFAULT_ORG_SWITCH_PATH(organizationId)
}
